import {
	Button,
	Input,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	Card,
	CardBody,
} from "@heroui/react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAppApi } from "src/appApi";
import { PAYMENT_TITLE, paidTotal, requestCost, TRequest } from "@shared/entities";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

const formSchema = z.object({
	cost: z.coerce.number().positive({ message: "סכום חייב להיות גדול מאפס" }),
});

type Form = z.infer<typeof formSchema>;

export function AdminRequestPaymentModal({
	request,
}: {
	request: TRequest;
	onSubmit?: () => void;
}) {
	const appApi = useAppApi();
	const [paymentLink, setPaymentLink] = useState<string | null>(null);
	const [isGeneratingLink, setIsGeneratingLink] = useState(false);
	const [copied, setCopied] = useState(false);

	const form = useForm<Form>({
		resolver: zodResolver(formSchema),
	});

	// Suggest what is still owed; the admin can change it.
	const cost = requestCost(request);
	const remaining = cost ? Math.max(cost - paidTotal(request), 0) : undefined;
	useEffect(() => {
		if (remaining && form.getValues("cost") === undefined) form.setValue("cost", remaining);
	}, [remaining, form]);

	const _onSubmit = async (data: Form) => {
		if (!request.id) return;

		// Then generate payment link
		setIsGeneratingLink(true);
		try {
			const paymentResult = await appApi.api.admin.generatePaymentLink({
				request,
				amount: data.cost,
				title: PAYMENT_TITLE,
			});
			console.log("paymentResult", paymentResult);

			if (paymentResult.success && paymentResult.paymentLink) {
				setPaymentLink(paymentResult.paymentLink);
			} else {
				form.setError("root", {
					message: paymentResult.errMessage || "שגיאה ביצירת קישור תשלום",
				});
			}
		} catch {
			form.setError("root", { message: "שגיאה ביצירת קישור תשלום" });
		} finally {
			setIsGeneratingLink(false);
		}
	};

	return (
		<ModalContent onSubmit={form.handleSubmit(_onSubmit, console.warn)} as={"form"}>
			{(onClose) => {
				return (
					<>
						<ModalHeader>בקש תשלום</ModalHeader>
						<ModalBody className="">
							{paymentLink ? (
								<div className="space-y-4">
									<Card className="bg-success-50 border border-success-200">
										<CardBody>
											<div className="flex flex-col gap-3">
												<div className="flex items-center gap-2">
													<Icon
														icon="lucide:check-circle"
														className="text-success"
														width={24}
													/>
													<p className="font-semibold text-success">
														קישור תשלום נוצר בהצלחה
													</p>
												</div>
												<div className="flex gap-2">
													<Input
														value={paymentLink}
														readOnly
														className="flex-1"
														endContent={
															<Button
																isIconOnly
																size="sm"
																variant="light"
																onPress={() => {
																	navigator.clipboard.writeText(paymentLink);
																}}
															>
																<Icon icon="lucide:copy" width={16} />
															</Button>
														}
													/>
												</div>
												<Button
													color="success"
													as="a"
													href={paymentLink}
													target="_blank"
													rel="noopener noreferrer"
													startContent={
														<Icon icon="lucide:external-link" width={16} />
													}
												>
													פתח קישור תשלום
												</Button>
											</div>
										</CardBody>
									</Card>
								</div>
							) : (
								<>
									<Input
										type="text"
										label="כותרת"
										value={PAYMENT_TITLE}
										isReadOnly
										description="הכותרת קבועה ומופיעה בעמוד התשלום ובקבלה"
									/>
									<Controller
										name="cost"
										control={form.control}
										render={({ field, fieldState }) => (
											<Input
												type="number"
												label="סכום לתשלום"
												placeholder="הכנס סכום לתשלום"
												startContent={
													<Icon
														icon="lucide:shekel-sign"
														className="h-4 w-4 text-default-400"
													/>
												}
												value={field.value?.toString() ?? ""}
												onChange={(e) => {
													const value =
														e.target.value === ""
															? undefined
															: Number(e.target.value);
													field.onChange(value);
												}}
												errorMessage={fieldState.error?.message}
												isInvalid={!!fieldState.error}
											/>
										)}
									/>
								</>
							)}
							{form.formState.errors.root && (
								<p className="text-danger text-sm mt-2">
									{form.formState.errors.root.message}
								</p>
							)}
						</ModalBody>
						<ModalFooter className="">
							{paymentLink ? (
								<>
									<Button
										variant="bordered"
										onPress={async () => {
											await navigator.clipboard.writeText(paymentLink);
											setCopied(true);
											setTimeout(() => {
												onClose();
											}, 500);
										}}
										startContent={
											<Icon icon={copied ? "lucide:check" : "lucide:copy"} width={16} />
										}
									>
										{copied ? "הועתק!" : "העתק קישור"}
									</Button>
									<Button color="primary" onPress={onClose}>
										סגור
									</Button>
								</>
							) : (
								<>
									<Button variant="light" onPress={onClose}>
										ביטול
									</Button>
									<Button
										isLoading={
											appApi.loading["admin.setRequest"] ||
											isGeneratingLink ||
											appApi.loading["admin.generatePaymentLink"]
										}
										type="submit"
										color="primary"
									>
										צור קישור תשלום
									</Button>
								</>
							)}
						</ModalFooter>
					</>
				);
			}}
		</ModalContent>
	);
}
